import React, { useState, useEffect, useMemo } from "react"
import createAxiosInstance from "../../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../../utils/api";
import { FiEdit, FiPlus, FiTrash2 } from "react-icons/fi"
import Pagination from "../../../../Components/Pagination"
import { Link, useNavigate } from "react-router-dom"
import useMyAgencyData from "../../../../hooks/useMyAgencyData";
import usePageFilter from "../../../../hooks/usePageFilter";
import useAgencies from "../../../../hooks/useAgencies";
import { responseDisplayName } from "../../../../utils/helper";
import Loader from "../../../../Components/Loader";
import { useFeature } from "../../../../hooks/useFeature";
import { showToast } from "../../../../Components/ToastNotification";
import useSocket from "../../../../services/socketService";
import { formatDateTime } from "../../../../utils/formatters";

const RequestReceive = () => {
  const isSender = useFeature("bedfinder:canSendRequest");
  const navigate = useNavigate();
  const myShelter = useMyAgencyData();
  const { agenciesById } = useAgencies();
  const axiosInstance = createAxiosInstance();

  const [request, setRequest] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowPerPage, setRowPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoding, setIsLoding] = useState(true);

  const { on } = useSocket("needs");

  useEffect(() => {
    const unsubscribe = on("message", (data) => {
      if (data.eventType === "request") {
        showToast("An agency has a new request.", "info");
        getRequestFromOthers();
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (myShelter) {
      getRequestFromOthers();
    }
  }, [myShelter]);

  const getRequestFromOthers = async () => {
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.BEDFINDER.GETREQUESTFROMOTHERS}?status=*&expand=need/assessments,responses`);

      const data = response.data; // Extract the actual data

      setRequest(data);
      setTotalPages(Math.ceil(data.length / rowPerPage));
      setIsLoding(false);
    } catch (error) {
      setIsLoding(false);
      console.log(error);
    }
  };

  const orderedRequests = useMemo(
    () =>
      request.map((req: any) => ({
        ...req,
        referenceUrl: `/bedfinder/beds-needed/review/${req.requestId}`,
        requestedBy: agenciesById[req.agencyId]?.name ?? "",
        myResponse:
          req.status === "closed"
            ? "-"
            : responseDisplayName[
            req.responses?.find((it: any) => it.agencyId == myShelter?.agencyId)
              ?.response ?? "unknown"
            ],
      })).sort((a: any, b: any) => b.nr - a.nr),
    [request, agenciesById],
  );

  const filteredRequests = usePageFilter(
    orderedRequests,
    (currentPage - 1) * rowPerPage,
    rowPerPage
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleOnClick = () => {
    isSender
      ? navigate("/bedfinder/new-request")
      : showToast(
        "You don't have permission to create a new request. Please contact your administrator.", "error"
      );
  }

  return (
    <div className=" bg-white">
      {isLoding && <Loader />}
      <div className="container py-4 md:py-10 2xl:py-[70px]">
        <div className="mb-5 2xl:mb-10 flex justify-center">
          <button onClick={handleOnClick} className="btn btn-primary bg-[#DD5226] hover:bg-orange-400 rounded-full">
            <FiPlus size={20} /> Find a Bed
          </button>
        </div>
        <div className="mb-4">
          <h3 className="text-gray-900 font-semibold text-base lg:text-lg 2xl:text-xl mb-2">All Bed Requests Received</h3>
        </div>
        <div className="relative md:overflow-hidden md:border md:border-borderGray rounded-xl">
          <table className="table_responsive_block w-full text-xs 2xl:text-sm text-left rtl:text-right">
            <thead className="text-xs 2xl:text-sm text-gray-600 bg-gray-100 border-b">
              <tr>
                <th scope="col" className="px-4 2xl:px-6 py-3 font-medium">Request #</th>
                <th scope="col" className="px-4 2xl:px-6 py-3 font-medium">Date/Time</th>
                <th scope="col" className="px-4 2xl:px-6 py-3 font-medium">Status</th>
                <th scope="col" className="px-4 2xl:px-6 py-3 font-medium">Requested By</th>
                <th scope="col" className="px-4 2xl:px-6 py-3 font-medium">Your Response</th>
                <th scope="col" className="px-4 2xl:px-6 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((request: any, index: number) => (
                <tr className={` border-b ${index % 2 == 0 ? 'bg-white' : 'bg-gray-50'}`} key={index}>
                  <td data-title='Request #' scope="row" className="px-4 2xl:px-6 py-3 font-medium text-primary cursor-pointer whitespace-nowrap">
                    <Link to={request.referenceUrl}> {request.nr} </Link>
                  </td>
                  <td data-title='Date/Time' className="px-4 2xl:px-6 py-3">{formatDateTime(request.createdAt)}</td>
                  <td data-title='Status' className="px-4 2xl:px-6 py-3">
                    {request.status === "open" && (
                      <span className="bg-success50 text-success text-xs 2xl:text-sm p-1 px-2 rounded-full flex items-center gap-1 w-fit font-medium">
                        Open
                      </span>
                    )}
                    {request.status === "closed" && (
                      <span className="bg-orange-50 text-error text-xs 2xl:text-sm p-1 px-2 rounded-full flex items-center gap-1 w-fit font-medium">
                        Closed
                      </span>
                    )}
                  </td>
                  <td data-title='Requested By' className="px-4 2xl:px-6 py-3">{request.requestedBy}</td>
                  <td data-title='Your Response' className="px-4 2xl:px-6 py-3">{request.myResponse}</td>
                  <td data-title='Action' className="px-4 2xl:px-6 py-3 flex space-x-1">
                    <Link to={request.referenceUrl}><button className="text-gray-700 text-xl size-10 flex items-center justify-center rounded-lg hover:bg-gray-100"><FiEdit /></button></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-5 flex justify-between items-center">
          <div></div>
          <Pagination totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      </div>
    </div>
  )
}

export default RequestReceive